import { useMemo, useState } from "react";
import { EyeOff } from "lucide-react";
import { AddToCollectionSheet } from "@/components/AddToCollectionSheet";
import { CalendarDrawer } from "@/components/events/CalendarDrawer";
import { EntityRemindersPanel } from "@/components/entity-display/sections/EntityRemindersPanel";
import type { LeafActionSet } from "@/components/layout/LeafActions";
import { UnifiedModal } from "@/components/ui/unified-modal";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useAddToCalendar } from "@/hooks/useAddToCalendar";
import { useEntityReminders } from "@/hooks/useEntityReminders";
import { useNativeShare } from "@/hooks/useNativeShare";
import { usePersonalItemActions } from "@/hooks/usePersonalItemActions";
import { useSkipReminders } from "@/hooks/useSkipReminders";
import { STUFF_DETAIL } from "@/routes";
import { normalizeEventData } from "@/utils/calendarUtils";
import { extractEventDates } from "@/utils/eventDateUtils";
import { getShareableUrlForPath } from "@/utils/share";
import { entityReminderPayload } from "./entityReminder";
import type { EntityDetailResult } from "./useEntityDetail";

/** One set of Stuff actions and editors, regardless of where the item appears. */
export function useEntityActions({ entityId, entity, onShare, onHide, requireAuth, loadReminderStatus = false }: {
  entityId: string;
  entity: EntityDetailResult["entity"];
  onShare?: () => void;
  onHide?: () => void;
  requireAuth?: (action: string, id: string, callback: () => void) => void;
  loadReminderStatus?: boolean;
}) {
  const { isAuthenticated } = useAuth();
  const { share } = useNativeShare();
  const skipReminders = useSkipReminders();
  const [menuOpen, setMenuOpen] = useState(false);
  const [remindersOpen, setRemindersOpen] = useState(false);
  const [collectionOpen, setCollectionOpen] = useState(false);
  const [hideOpen, setHideOpen] = useState(false);
  const personalActions = usePersonalItemActions("entity", entityId, menuOpen);
  const calendar = useAddToCalendar(entity, entityId);
  const calendarStart = entity ? normalizeEventData({ sourceEntity: entity, entityId }).dtstart : null;
  const canCalendar = Boolean(calendarStart && Number.isFinite(Date.parse(calendarStart)));
  const payload = useMemo(() => entityReminderPayload(entity, entityId), [entity, entityId]);
  const { count: reminderCount } = useEntityReminders({
    entityId, payload, enabled: isAuthenticated && !skipReminders && (loadReminderStatus || menuOpen),
  });
  const gated = (action: string, callback: () => void) => () => {
    if (requireAuth) requireAuth(action, entityId, callback);
    else if (isAuthenticated) callback();
  };
  const openReminders = gated("remind", () => setRemindersOpen(true));
  const canAccountAction = isAuthenticated || Boolean(requireAuth);
  const actions: LeafActionSet = {
    ...personalActions,
    share: { onSelect: onShare ?? (() => { void share(getShareableUrlForPath(STUFF_DETAIL({ entityId }))); }) },
    remind: !skipReminders && canAccountAction ? { onSelect: openReminders, count: reminderCount } : undefined,
    collect: canAccountAction ? { onSelect: gated("save", () => setCollectionOpen(true)) } : undefined,
    calendar: canCalendar && canAccountAction ? { onSelect: gated("calendar", () => calendar.setDrawerOpen(true)) } : undefined,
    hide: isAuthenticated && onHide ? { onSelect: () => setHideOpen(true) } : undefined,
  };
  // Keep editors outside card Links, so portal events cannot navigate the row.
  const panels = <>
    <EntityRemindersPanel open={remindersOpen} onOpenChange={setRemindersOpen} entityId={entityId}
      anchorTime={extractEventDates(entity)?.start ?? null} reminderPayload={payload} />
    {collectionOpen && <AddToCollectionSheet open={collectionOpen} onOpenChange={setCollectionOpen}
      items={[{ type: "entity", id: entityId }]} />}
    <CalendarDrawer open={calendar.drawerOpen} onOpenChange={calendar.setDrawerOpen}
      onGoogleCalendar={calendar.handleGoogleCalendar} onAppleCalendar={calendar.handleAppleCalendar} />
    <UnifiedModal open={hideOpen} onOpenChange={setHideOpen} icon={<EyeOff />} title="Hide this item?"
      description="This hides it from your account. Its information and collection memberships are kept." variant="destructive">
      <div className="flex gap-3">
        <Button variant="secondary" size="modal" className="flex-1" onClick={() => setHideOpen(false)}>Cancel</Button>
        <Button variant="destructive" size="modal" className="flex-1" onClick={() => { setHideOpen(false); onHide?.(); }}>Hide</Button>
      </div>
    </UnifiedModal>
  </>;
  return { actions, panels, setMenuOpen, reminderCount, openReminders, setCalendarOpen: calendar.setDrawerOpen };
}
